<template>
  <AppModal
    :title="title"
    content-class="provider-dialog"
    position="right"
    no-maximized
    show-close-btn
    v-bind="$attrs"
    @close="$emit('close')"
  >
    <q-form
      ref="form"
      greedy
    >
      <div class="provider-dialog__provider">
        <div>{{ selectLabel }}</div>
        <AppSelect
          v-model="providerData.type"
          :options="options"
          emit-value
          map-options
        />
      </div>
      <div
        :class="['provider-dialog__provider-fields', {
          'provider-dialog__provider-fields_empty': !hasProvider,
        }]"
      >
        <q-icon
          v-if="!hasProvider"
          name="fa fa-pen"
          color="grey"
          size="70px"
        />
        <template v-else>
          <div
            v-for="field in fields"
            :key="field.name"
            class="provider-dialog__provider-field"
          >
            <div>{{ $t(field.titleKey) }}</div>
            <AppInput
              v-model="providerData[field.name]"
              :rules="field.options.rules"
            />
          </div>
        </template>
      </div>
    </q-form>
    <template
      v-if="hasProvider"
      #footer
    >
      <AppButton
        v-close-popup
        :label="$t('button.cancel')"
        :class="`provider-dialog__btn-cancel`"
        color="white"
        text-color="black"
      />
      <AppButton
        :label="submitLabel"
        :loading="loading"
        class="col"
        color="primary"
        text-color="black"
        @click="create"
      />
    </template>
  </AppModal>
</template>

<script>
import AppInput from 'common/components/AppInput';
import AppModal from 'common/components/AppModal';
import AppSelect from 'common/components/AppSelect';
import AppButton from 'common/components/buttons/AppButton';
import { INVALID_AUTORESPONDER_PROVIDER_API_KEY, INVALID_WEBINAR_PROVIDER_API_KEY } from 'common/enums/errorCodes';
import handleError from 'common/helpers/handleError';
import { notifyError } from 'common/helpers/notify';
import { mapState } from 'vuex';

export default {
  name: 'NewProviderDialog',
  components: {
    AppInput,
    AppButton,
    AppSelect,
    AppModal,
  },
  props: {
    options: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    selectLabel: {
      type: String,
      required: true,
    },
    submitLabel: {
      type: String,
      required: true,
    },
    createFn: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      providerData: {
        type: undefined,
      },
    };
  },
  computed: {
    ...mapState('app', ['layout']),
    hasProvider() {
      return !!this.providerData.type;
    },
    fields() {
      if (this.hasProvider) {
        return this.options
          .find((item) => item.value === this.providerData.type)
          .fields;
      }
      return [];
    },
  },
  watch: {
    'providerData.type'() {
      this.configureFields();
    },
  },
  methods: {
    async create() {
      try {
        const isValid = await this.$refs.form.validate();
        if (!isValid) {
          return;
        }
        this.loading = true;

        const title = this.providerData.title;

        if (this.providerData.type === ENUMS.WEBINAR_TYPES.ZOOM) {
          // eslint-disable-next-line no-restricted-globals
          location.href = `${this.layout.zoomOauthLink}&state=title=${title};from=${this.$route.fullPath};`;
          return;
        }

        const fields = { ...this.providerData };
        delete fields.title;

        const { data } = await this.createFn({
          title,
          meta: fields,
        });
        this.providerData.type = undefined;
        this.$emit('input', data.id);
        this.$emit('close');
      } catch (error) {
        const code = error.response?.data?.code;
        if ([INVALID_AUTORESPONDER_PROVIDER_API_KEY, INVALID_WEBINAR_PROVIDER_API_KEY].includes(code)) {
          notifyError(this.$t('notification.integrationFailed'));
        } else {
          handleError(error);
        }
      } finally {
        this.loading = false;
      }
    },
    configureFields() {
      Object.keys(this.providerData)
        .filter((key) => key !== 'type')
        .forEach((key) => this.$delete(this.providerData, key));

      if (this.hasProvider) {
        this.fields.forEach((field) => this.$set(this.providerData, field.name, undefined));
        this.providerData.title = this.options.find((option) => option.value === this.providerData.type).label;
      }
    },
  },
};
</script>

<style lang="scss">
.provider-dialog {
  width: 512px;

  &__btn-cancel {
    border: 1px solid $grey-2;
  }
  &__provider {
    padding: 30px 45px;
    display: grid;
    grid-template-columns: 1fr 2fr;
    align-items: center;
    border-bottom: 1px solid $grey-2;

    .app-select {
      flex: 1;
    }

    &-fields {
      padding: 30px 45px;

      &:not(&_empty) {
        border-bottom: 1px solid $grey-2;
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      &_empty {
        padding: 116px 199px;
      }
    }

    &-field {
      display: grid;
      grid-template-columns: 1fr 2fr;
      align-items: center;
    }
  }

  .modal-footer {
    padding: 30px 25px;
    gap: 12px;
  }
  .bg-transparent {
    overflow: hidden;
  }
}
</style>
